/**
 * Stable ids for the suggested prompts and the prompt library.
 * Display text and the query sent to the model live in the TimePilot catalogs
 * under prompts.suggested.*, prompts.library.*, prompts.category.*, and
 * prompts.subCategory.*. The prompt-library flag decides whether the lists
 * are shown. prompt-library-prompts is the full list of entries; only
 * those marked enabled are included.
 */

import { translateTimePilotMessage } from "./timepilotCatalogs";

export const SUGGESTED_PROMPT_IDS = ["summarize", "timeline", "evidence"] as const;

export type SuggestedPromptId = (typeof SUGGESTED_PROMPT_IDS)[number];

export const PROMPT_CATEGORY_IDS = [
  "prosecution",
  "jailCalls",
  "fraud",
  "osint",
  "education",
  "suggested",
] as const;

export type PromptCategoryId = (typeof PROMPT_CATEGORY_IDS)[number];

export const PROMPT_SUBCATEGORY_IDS = [
  "report",
  "threats",
  "language",
  "contacts",
  "crime",
  "financialInformation",
  "charges",
  "trial",
  "evidence",
  "investigation",
  "data",
  "poiEvaluation",
  "suspects",
  "witnesses",
  "defense",
  "teaching",
  "summary",
  "timeline",
] as const;

export type PromptSubCategoryId = (typeof PROMPT_SUBCATEGORY_IDS)[number];

export const SUGGESTED_CATEGORY_ID: PromptCategoryId = "suggested";

/** Keyword-filter value that means every category. Not a catalog string. */
export const ALL_PROMPT_KEYWORDS = "All Keywords";

export type LibraryPromptRef = {
  id: string;
  category: PromptCategoryId;
  subCategory: PromptSubCategoryId;
  /** Short label and query come from prompts.suggested.<suggestedId>.* */
  suggestedId?: SuggestedPromptId;
};

export const LIBRARY_PROMPTS: readonly LibraryPromptRef[] = [
  { id: "prosecution-videoInterview", category: "prosecution", subCategory: "report" },
  { id: "jailCalls-suspiciousActivity", category: "jailCalls", subCategory: "threats" },
  { id: "jailCalls-sentimentAnalysis", category: "jailCalls", subCategory: "threats" },
  { id: "jailCalls-threats", category: "jailCalls", subCategory: "threats" },
  { id: "jailCalls-incriminatingStatements", category: "jailCalls", subCategory: "threats" },
  { id: "jailCalls-charges", category: "jailCalls", subCategory: "threats" },
  { id: "jailCalls-commonLanguage", category: "jailCalls", subCategory: "language" },
  { id: "jailCalls-language", category: "jailCalls", subCategory: "language" },
  { id: "jailCalls-contacts", category: "jailCalls", subCategory: "contacts" },
  { id: "jailCalls-relationships", category: "jailCalls", subCategory: "contacts" },
  { id: "jailCalls-events", category: "jailCalls", subCategory: "contacts" },
  { id: "jailCalls-locations", category: "jailCalls", subCategory: "contacts" },
  { id: "fraud-repeatedTransactions", category: "fraud", subCategory: "crime" },
  { id: "fraud-financialCrime", category: "fraud", subCategory: "crime" },
  { id: "fraud-suspiciousActivity", category: "fraud", subCategory: "crime" },
  { id: "fraud-financialInformation", category: "fraud", subCategory: "financialInformation" },
  { id: "fraud-accounts", category: "fraud", subCategory: "financialInformation" },
  { id: "fraud-transactionCount", category: "fraud", subCategory: "financialInformation" },
  { id: "fraud-complaintReview", category: "fraud", subCategory: "investigation" },
  { id: "fraud-audioTranscript", category: "fraud", subCategory: "investigation" },
  { id: "fraud-prescribingTrends", category: "fraud", subCategory: "investigation" },
  { id: "prosecution-incidents", category: "prosecution", subCategory: "charges" },
  { id: "prosecution-witnessOrder", category: "prosecution", subCategory: "trial" },
  { id: "prosecution-chargeStrength", category: "prosecution", subCategory: "charges" },
  { id: "prosecution-defenseCase", category: "prosecution", subCategory: "charges" },
  { id: "prosecution-exculpatory", category: "prosecution", subCategory: "charges" },
  { id: "prosecution-crimes", category: "prosecution", subCategory: "evidence" },
  { id: "prosecution-leads", category: "prosecution", subCategory: "evidence" },
  { id: "prosecution-incriminatingEvidence", category: "prosecution", subCategory: "evidence" },
  { id: "prosecution-potentialCharges", category: "prosecution", subCategory: "evidence" },
  { id: "osint-radicalization", category: "osint", subCategory: "poiEvaluation" },
  { id: "prosecution-timeline", category: "prosecution", subCategory: "investigation" },
  { id: "prosecution-preliminaryReport", category: "prosecution", subCategory: "investigation" },
  { id: "prosecution-identifiers", category: "prosecution", subCategory: "data" },
  { id: "osint-dataExtraction", category: "osint", subCategory: "data" },
  { id: "prosecution-onlineActivity", category: "prosecution", subCategory: "data" },
  { id: "osint-socialMedia", category: "osint", subCategory: "data" },
  { id: "osint-alarmingContent", category: "osint", subCategory: "data" },
  { id: "osint-caseEvidenceList", category: "osint", subCategory: "evidence" },
  { id: "osint-caseEvidenceInventory", category: "osint", subCategory: "evidence" },
  { id: "osint-incriminatingEvidence", category: "osint", subCategory: "evidence" },
  { id: "osint-pois", category: "osint", subCategory: "poiEvaluation" },
  { id: "osint-behavioralAssessment", category: "osint", subCategory: "poiEvaluation" },
  { id: "osint-travel", category: "osint", subCategory: "poiEvaluation" },
  { id: "osint-connections", category: "osint", subCategory: "poiEvaluation" },
  { id: "osint-threats", category: "osint", subCategory: "poiEvaluation" },
  { id: "osint-language", category: "osint", subCategory: "report" },
  { id: "osint-threatReport", category: "osint", subCategory: "report" },
  { id: "osint-narrative", category: "osint", subCategory: "report" },
  { id: "osint-warrant", category: "osint", subCategory: "report" },
  { id: "education-evidenceCollected", category: "education", subCategory: "evidence" },
  { id: "education-keySuspects", category: "education", subCategory: "suspects" },
  { id: "education-suspectEvents", category: "education", subCategory: "suspects" },
  { id: "education-evidenceGaps", category: "education", subCategory: "evidence" },
  { id: "education-witnessCredibility", category: "education", subCategory: "witnesses" },
  { id: "education-reasonableDoubt", category: "education", subCategory: "defense" },
  { id: "education-evidenceExclusion", category: "education", subCategory: "evidence" },
  { id: "education-syllabus", category: "education", subCategory: "teaching" },
  { id: "education-discussionQuestions", category: "education", subCategory: "teaching" },
  { id: "education-lessonPlan", category: "education", subCategory: "teaching" },
  { id: "education-lectureBrief", category: "education", subCategory: "teaching" },
  { id: "education-assessments", category: "education", subCategory: "teaching" },
  { id: "education-hypothetical", category: "education", subCategory: "teaching" },
  { id: "education-learningObjectives", category: "education", subCategory: "teaching" },
  { id: "education-classroomExercise", category: "education", subCategory: "teaching" },
  { id: "suggested-summarize", category: "suggested", subCategory: "summary", suggestedId: "summarize" },
  { id: "suggested-timeline", category: "suggested", subCategory: "timeline", suggestedId: "timeline" },
  { id: "suggested-evidence", category: "suggested", subCategory: "evidence", suggestedId: "evidence" },
  { id: "prosecution-deposition", category: "prosecution", subCategory: "trial" },
  { id: "prosecution-crossExamination", category: "prosecution", subCategory: "trial" },
  { id: "prosecution-officerReports", category: "prosecution", subCategory: "report" },
  { id: "prosecution-victimology", category: "prosecution", subCategory: "report" },
];

/** LaunchDarkly still gates the lists. A non-empty array or `true` shows them. */
export function isPromptFlagOn(value: unknown): boolean {
  if (value === true) return true;
  return Array.isArray(value) && value.length > 0;
}

/** One row of the prompt-library-prompts flag. `label` is for editors; matching uses `id`. */
export type PromptLibraryFlagEntry = {
  id: string;
  label: string;
  enabled: boolean;
};

function libraryPromptShortLabel(entry: LibraryPromptRef): string {
  if (entry.suggestedId) {
    return translateTimePilotMessage("en", `prompts.suggested.${entry.suggestedId}.label`);
  }
  return translateTimePilotMessage("en", `prompts.library.${entry.id}.short`);
}

/**
 * Parses the prompt-library-prompts value.
 * `true`, a missing value, or anything that is not an array means every prompt.
 * An array shows a prompt only when an object with that id has `enabled: true`.
 */
function promptFlagList(flagValue: unknown): unknown[] | null {
  let value = flagValue;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return null;
    try {
      value = JSON.parse(trimmed) as unknown;
    } catch {
      return null;
    }
  }
  if (value === true || value == null) return null;
  if (!Array.isArray(value)) return null;
  return value;
}

export function enabledLibraryPrompts(
  entries: readonly LibraryPromptRef[],
  flagValue: unknown,
): LibraryPromptRef[] {
  const listed = promptFlagList(flagValue);
  if (!listed) return [...entries];
  const enabled = new Set<string>();
  for (const item of listed) {
    if (!item || typeof item !== "object") continue;
    const record = item as { id?: unknown; enabled?: unknown };
    if (typeof record.id === "string" && record.enabled === true) enabled.add(record.id);
  }
  return entries.filter((entry) => enabled.has(entry.id));
}

/** Starter payload for the prompt-library-prompts flag: every prompt on, English short text in `label`. */
export function promptLibraryFlagTemplate(
  entries: readonly LibraryPromptRef[] = LIBRARY_PROMPTS,
): PromptLibraryFlagEntry[] {
  return entries.map((entry) => ({
    id: entry.id,
    label: libraryPromptShortLabel(entry),
    enabled: true,
  }));
}

export function filterLibraryPrompts(
  entries: readonly LibraryPromptRef[],
  selectedCategory: string,
): LibraryPromptRef[] {
  if (selectedCategory === ALL_PROMPT_KEYWORDS) return [...entries];
  const separator = selectedCategory.indexOf(" > ");
  if (separator !== -1) {
    const category = selectedCategory.slice(0, separator);
    const subCategory = selectedCategory.slice(separator + 3);
    return entries.filter(
      (entry) => entry.category === category && entry.subCategory === subCategory,
    );
  }
  return entries.filter((entry) => entry.category === selectedCategory);
}

/** Filter values: every category, then each subcategory except under Suggested Prompts. */
export function libraryKeywordValues(entries: readonly LibraryPromptRef[]): string[] {
  const subcategories = new Map<string, Set<string>>();
  for (const entry of entries) {
    const existing = subcategories.get(entry.category);
    if (existing) {
      existing.add(entry.subCategory);
    } else {
      subcategories.set(entry.category, new Set([entry.subCategory]));
    }
  }
  const values = [ALL_PROMPT_KEYWORDS];
  for (const category of [...subcategories.keys()].sort()) {
    values.push(category);
    if (category === SUGGESTED_CATEGORY_ID) continue;
    for (const subCategory of [...(subcategories.get(category) ?? [])].sort()) {
      values.push(`${category} > ${subCategory}`);
    }
  }
  return values;
}
